import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApproveTimeReportData } from "services/api/company/timeReports.service";
import { Api } from "services/api/api.service";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  mergeIsoDateWithTimeInput,
  minutesBetweenIsoStrict,
  normalizeTimeReportTimesOvernight,
  parseIsoMillis,
  toTimeInputValue,
} from "web-apps/company/utils/timeAndDate";
import {
  deriveTimeReportDetails,
  mapTimeReportDetailToPanelModel,
  type TimeReportDetailsPanelModel,
} from "./timeReportDetailsPanel.mapper";
import { resolveTimeReportsDisplaySettings } from "web-apps/company/components/timeReportsV2/timeReports.settings.logic";

interface UseTimeReportDetailsPanelLogicParams {
  timeReportId: number | null;
  onClose: () => void;
  onActionCompleted?: () => void;
}

interface UseTimeReportDetailsPanelLogicResult {
  detail: TimeReportDetailsPanelModel | null;
  avatarUrl: string | null;
  isLoading: boolean;
  hasError: boolean;
  derived: ReturnType<typeof deriveTimeReportDetails>;
  handleClose: () => void;
  fetchDetails: () => Promise<void>;
  editMode: {
    isEditMode: boolean;
    editedReportedStart: string;
    editedReportedEnd: string;
    editedBreakStart: string;
    editedBreakEnd: string;
    setEditedReportedStart: (value: string) => void;
    setEditedReportedEnd: (value: string) => void;
    setEditedBreakStart: (value: string) => void;
    setEditedBreakEnd: (value: string) => void;
    enterEditMode: () => void;
    cancelEditMode: () => void;
    saveEditChanges: () => Promise<void>;
  };
  actions: {
    isActionLoading: boolean;
    canRunActions: boolean;
    canApproveAction: boolean;
    handleRetract: (comment: string) => Promise<boolean>;
    handleReset: (comment: string) => Promise<boolean>;
    handleReject: (comment: string) => Promise<boolean>;
    handleApprove: (comment: string) => Promise<boolean>;
  };
  feedback: {
    feedbackState: "success" | "error" | null;
    setFeedbackState: (value: "success" | "error" | null) => void;
  };
  absenceModal: {
    isAbsenceModalOpen: boolean;
    setIsAbsenceModalOpen: (value: boolean) => void;
  };
  settings: {
    deviationToleranceMinutes: number;
    includeFlexTimeDeviations: boolean;
  };
}

interface ActionContext {
  companyId: number;
  timeReportId: number;
}

interface EditedTimesPayload {
  startTime: string;
  endTime: string;
  breakStartTime: string;
  breakEndTime: string;
}

interface RunActionOptions {
  notifyParentOnSuccess?: boolean;
}

export const useTimeReportDetailsPanelLogic = (
  params: UseTimeReportDetailsPanelLogicParams,
): UseTimeReportDetailsPanelLogicResult => {
  const onClose = params.onClose;
  const onActionCompleted = params.onActionCompleted;
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id ?? null;
  const selectedTimeReportId = params.timeReportId;
  const [detail, setDetail] = useState<TimeReportDetailsPanelModel | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [feedbackState, setFeedbackState] = useState<
    "success" | "error" | null
  >(null);
  const [editedReportedStart, setEditedReportedStart] = useState("");
  const [editedReportedEnd, setEditedReportedEnd] = useState("");
  const [editedBreakStart, setEditedBreakStart] = useState("");
  const [editedBreakEnd, setEditedBreakEnd] = useState("");
  const latestRequestIdRef = useRef(0);
  const actionLockRef = useRef(false);

  const handleClose = useCallback((): void => {
    setIsEditMode(false);
    onClose();
  }, [onClose]);

  const avatarUrl = useWorkerAvatar({
    workerExternalId: detail?.worker.externalId ?? null,
    isWorker: true,
    initialUrl: null,
  });

  useEffect(() => {
    setIsEditMode(false);
    setDetail(null);
    setHasError(false);
    setFeedbackState(null);
    setIsAbsenceModalOpen(false);
    actionLockRef.current = false;
    setIsActionLoading(false);
  }, [companyId, selectedTimeReportId]);

  useCloseOnEscape({
    isOpen: selectedTimeReportId != null,
    onClose: handleClose,
    preventDefault: true,
  });

  const fetchDetails = useCallback(async (): Promise<void> => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    if (!companyId || !selectedTimeReportId) {
      setDetail(null);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    try {
      const res = await Api().company.timeReports.getTimeReportV2(
        companyId,
        selectedTimeReportId,
      );
      if (requestId !== latestRequestIdRef.current) return;
      setDetail(mapTimeReportDetailToPanelModel(res.data));
    } catch (_error: unknown) {
      if (requestId !== latestRequestIdRef.current) return;
      setHasError(true);
      setDetail(null);
    } finally {
      if (requestId !== latestRequestIdRef.current) return;
      setIsLoading(false);
    }
  }, [companyId, selectedTimeReportId]);

  useEffect(() => {
    void fetchDetails();
  }, [fetchDetails]);

  const reportedStartForEdit = detail?.startTime ?? null;
  const reportedEndForEdit = detail?.endTime ?? null;
  const breakStartForEdit = detail?.breakStartTime ?? null;
  const breakEndForEdit = detail?.breakEndTime ?? null;

  useEffect(() => {
    if (!isEditMode) return;
    setEditedReportedStart(toTimeInputValue(reportedStartForEdit));
    setEditedReportedEnd(toTimeInputValue(reportedEndForEdit));
    setEditedBreakStart(toTimeInputValue(breakStartForEdit));
    setEditedBreakEnd(toTimeInputValue(breakEndForEdit));
  }, [
    isEditMode,
    reportedStartForEdit,
    reportedEndForEdit,
    breakStartForEdit,
    breakEndForEdit,
  ]);

  const derived = useMemo(
    () =>
      deriveTimeReportDetails({
        detail,
        isEditMode,
        editedReportedStart,
        editedReportedEnd,
        editedBreakStart,
        editedBreakEnd,
      }),
    [
      detail,
      isEditMode,
      editedReportedStart,
      editedReportedEnd,
      editedBreakStart,
      editedBreakEnd,
    ],
  );

  const timeReportId = derived.timeReportId;
  const canRunActions = Boolean(companyId && detail && timeReportId);
  const displaySettings = useMemo(
    () => resolveTimeReportsDisplaySettings(companyState.companySettings),
    [companyState.companySettings],
  );

  const getActionContext = (): ActionContext | null => {
    if (!companyId || !timeReportId) {
      return null;
    }

    return {
      companyId,
      timeReportId,
    };
  };

  const buildApprovePayload = (
    comment: string,
  ): ApproveTimeReportData | null => {
    const startTime = derived.reportedStart;
    const endTime = derived.reportedEnd;
    const breakStartTime = derived.breakStart;
    const breakEndTime = derived.breakEnd;

    // All time strings, including break boundaries, are required by domain rules.
    if (
      startTime == null ||
      endTime == null ||
      breakStartTime == null ||
      breakEndTime == null
    ) {
      return null;
    }

    const startBreakMillis = parseIsoMillis(breakStartTime);
    const endBreakMillis = parseIsoMillis(breakEndTime);
    if (startBreakMillis == null || endBreakMillis == null) {
      return null;
    }

    // Equal break start/end should be treated as a valid 0-minute break.
    const breakMinutes =
      startBreakMillis === endBreakMillis
        ? 0
        : minutesBetweenIsoStrict(breakStartTime, breakEndTime);
    if (breakMinutes == null) return null;

    return {
      comment,
      isEditedByCompany: true,
      startTime,
      endTime,
      breakTime: breakMinutes,
      breakStartTime,
      breakEndTime,
    };
  };
  const approvePayload = buildApprovePayload("");
  const canApproveAction =
    canRunActions && derived.showApproveAction && approvePayload != null;

  const showActionError = (): void => {
    setFeedbackState("error");
  };

  const showActionSuccess = (): void => {
    setFeedbackState("success");
  };

  const runActionWithContext = async (
    action: (context: ActionContext) => Promise<boolean>,
    actionContext: ActionContext,
  ): Promise<boolean> => {
    const actionSucceeded = await action(actionContext);
    if (!actionSucceeded) {
      showActionError();
      return false;
    }
    showActionSuccess();
    return true;
  };

  const buildEditedTimesPayload = (): EditedTimesPayload | null => {
    const nextStart = mergeIsoDateWithTimeInput(
      derived.reportedStart,
      editedReportedStart,
    );
    const nextEnd = mergeIsoDateWithTimeInput(
      derived.reportedEnd,
      editedReportedEnd,
    );
    const nextBreakStart = mergeIsoDateWithTimeInput(
      derived.breakStart,
      editedBreakStart,
    );
    const nextBreakEnd = mergeIsoDateWithTimeInput(
      derived.breakEnd,
      editedBreakEnd,
    );

    if (!nextStart || !nextEnd || !nextBreakStart || !nextBreakEnd) {
      return null;
    }

    const normalizedTimes = normalizeTimeReportTimesOvernight({
      startTime: nextStart,
      endTime: nextEnd,
      breakStartTime: nextBreakStart,
      breakEndTime: nextBreakEnd,
    });
    if (!normalizedTimes) {
      return null;
    }

    return {
      startTime: normalizedTimes.startTime,
      endTime: normalizedTimes.endTime,
      breakStartTime: normalizedTimes.breakStartTime,
      breakEndTime: normalizedTimes.breakEndTime,
    };
  };

  const runAction = async (
    action: (context: ActionContext) => Promise<boolean>,
    options?: RunActionOptions,
  ): Promise<boolean> => {
    const actionContext = getActionContext();
    if (!canRunActions || !actionContext || isActionLoading) return false;
    if (actionLockRef.current) return false;
    const shouldNotifyParent = options?.notifyParentOnSuccess ?? true;

    actionLockRef.current = true;
    setIsActionLoading(true);
    let hasActionSucceeded = false;

    try {
      hasActionSucceeded = await runActionWithContext(action, actionContext);
    } catch (_error: unknown) {
      showActionError();
    } finally {
      if (hasActionSucceeded) {
        try {
          await fetchDetails();
          if (shouldNotifyParent) {
            onActionCompleted?.();
          }
        } catch (_error: unknown) {
          hasActionSucceeded = false;
          showActionError();
        }
      }
      actionLockRef.current = false;
      setIsActionLoading(false);
    }
    return hasActionSucceeded;
  };

  const enterEditMode = (): void => {
    if (!canRunActions || !derived.canEdit) return;
    setEditedReportedStart(toTimeInputValue(derived.reportedStart));
    setEditedReportedEnd(toTimeInputValue(derived.reportedEnd));
    setEditedBreakStart(toTimeInputValue(derived.breakStart));
    setEditedBreakEnd(toTimeInputValue(derived.breakEnd));
    setIsEditMode(true);
  };

  const cancelEditMode = (): void => {
    setIsEditMode(false);
  };

  const saveEditChanges = async (): Promise<void> => {
    if (
      !canRunActions ||
      !derived.canEdit ||
      !derived.isEditValid ||
      !derived.hasReportedTimeChanges ||
      !companyId ||
      !timeReportId
    ) {
      return;
    }

    await runAction(
      async (context): Promise<boolean> => {
        const editedTimesPayload = buildEditedTimesPayload();
        if (!editedTimesPayload) {
          return false;
        }

        await Api().company.timeReports.editTimeReport(
          context.companyId,
          context.timeReportId,
          editedTimesPayload,
        );

        setIsEditMode(false);
        return true;
      },
      { notifyParentOnSuccess: false },
    );
  };

  const handleRetract = async (comment: string): Promise<boolean> => {
    return runAction(async (context): Promise<boolean> => {
      await Api().company.timeReports.withdrawTimeReport(
        context.companyId,
        context.timeReportId,
        comment,
      );
      return true;
    });
  };

  const handleReset = async (comment: string): Promise<boolean> => {
    return runAction(async (context): Promise<boolean> => {
      await Api().company.timeReports.reopenTimeReport(
        context.companyId,
        context.timeReportId,
        comment,
      );
      return true;
    });
  };

  const handleReject = async (comment: string): Promise<boolean> => {
    return runAction(async (context): Promise<boolean> => {
      await Api().company.timeReports.rejectTimeReport(
        context.companyId,
        context.timeReportId,
        comment,
      );
      return true;
    });
  };

  const handleApprove = async (comment: string): Promise<boolean> => {
    return runAction(async (context): Promise<boolean> => {
      const approvePayloadWithComment = buildApprovePayload(comment);
      if (!approvePayloadWithComment) {
        return false;
      }
      await Api().company.timeReports.approveTimeReport(
        context.companyId,
        context.timeReportId,
        approvePayloadWithComment,
      );
      return true;
    });
  };

  return {
    detail,
    avatarUrl,
    isLoading,
    hasError,
    derived,
    handleClose,
    fetchDetails,
    editMode: {
      isEditMode,
      editedReportedStart,
      editedReportedEnd,
      editedBreakStart,
      editedBreakEnd,
      setEditedReportedStart,
      setEditedReportedEnd,
      setEditedBreakStart,
      setEditedBreakEnd,
      enterEditMode,
      cancelEditMode,
      saveEditChanges,
    },
    actions: {
      isActionLoading,
      canRunActions,
      canApproveAction,
      handleRetract,
      handleReset,
      handleReject,
      handleApprove,
    },
    feedback: {
      feedbackState,
      setFeedbackState,
    },
    absenceModal: {
      isAbsenceModalOpen,
      setIsAbsenceModalOpen,
    },
    settings: {
      deviationToleranceMinutes: displaySettings.deviationToleranceMinutes,
      includeFlexTimeDeviations: displaySettings.includeFlexTimeDeviations,
    },
  };
};
